import type { ProducerView } from "@/lib/producers";

type PaginatedStatus = "LoadingFirstPage" | "CanLoadMore" | "LoadingMore" | "Exhausted";

function parsePageNumber(page: string | number): number {
  const raw = typeof page === "string" ? Number.parseInt(page, 10) : page;
  if (!Number.isFinite(raw) || Number.isNaN(raw) || raw < 1) {
    return 1;
  }
  return Math.floor(raw);
}

interface PaginationSlice<T> {
  pageItems: T[];
  startIndex: number;
  endIndex: number;
  estimatedTotal: number;
}

function sliceProducersPage(
  producers: ProducerView[],
  currentPage: number,
  pageSize: number,
  hasMore: boolean,
): PaginationSlice<ProducerView> {
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pageItems = producers.slice(startIndex, endIndex);
  const estimatedTotal = hasMore ? Math.max(producers.length, endIndex) : producers.length;

  return {
    pageItems,
    startIndex,
    endIndex,
    estimatedTotal,
  };
}

function shouldLoadAdditionalPage(
  hasMore: boolean,
  loadedCount: number,
  endIndex: number,
): boolean {
  return hasMore && loadedCount < endIndex;
}

function shouldPrefetchNextPage(
  hasMore: boolean,
  loadedCount: number,
  currentPage: number,
  pageSize: number,
): boolean {
  if (!hasMore) {
    return false;
  }
  const nextThreshold = (currentPage + 1) * pageSize;
  return loadedCount < nextThreshold;
}

interface ProducersViewModel {
  pageProducers: ProducerView[];
  paginationInfo: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  shouldLoadMore: boolean;
  shouldPrefetch: boolean;
  isLoadingProducers: boolean;
}

interface BuildViewModelArgs {
  producers: ProducerView[];
  page: string;
  pageSize: number;
  status: PaginatedStatus;
}

/**
 * Slices the loaded producers into the requested page and decides whether more must be
 * fetched. Filtering happens on the server; this only pages through what was loaded.
 */
export function buildProducersViewModel({
  producers,
  page,
  pageSize,
  status,
}: BuildViewModelArgs): ProducersViewModel {
  const hasMore = status === "CanLoadMore";
  // Once every row is loaded, a stale `?page=` past the end shows the last page, not an empty one.
  const lastPage = Math.max(1, Math.ceil(producers.length / pageSize));
  const requestedPage = parsePageNumber(page);
  const currentPage = status === "Exhausted" ? Math.min(requestedPage, lastPage) : requestedPage;

  const slice = sliceProducersPage(producers, currentPage, pageSize, hasMore);

  const shouldLoadMore = shouldLoadAdditionalPage(hasMore, producers.length, slice.endIndex);
  const shouldPrefetch = shouldPrefetchNextPage(hasMore, producers.length, currentPage, pageSize);

  const isLoadingProducers = status === "LoadingFirstPage";

  return {
    pageProducers: slice.pageItems,
    paginationInfo: {
      total: slice.estimatedTotal,
      limit: pageSize,
      offset: slice.startIndex,
      hasMore,
    },
    shouldLoadMore,
    shouldPrefetch,
    isLoadingProducers,
  };
}
